/**
 * Mirrors backend app/core/permissions.py exactly.
 *
 * These strings gate UI affordances only — every mutation is re-checked on the
 * server, which stays the source of truth.
 */
export const Permission = {
  // read
  VIEW_FINANCIALS: 'financials:view',
  VIEW_ALL_BUSINESS_UNITS: 'units:view_all',
  VIEW_IMPORT_HISTORY: 'import:view_history',
  VIEW_AUDIT: 'audit:view',
  EXPORT_REPORTS: 'reports:export',

  // collaboration
  COMMENT: 'comment:write',

  // import
  UPLOAD_IMPORT: 'import:upload',
  CONFIRM_IMPORT: 'import:confirm',
  MANAGE_MAPPING: 'import:manage_mapping',
  ROLLBACK_IMPORT: 'import:rollback',

  // financial data
  EDIT_FINANCIAL_DATA: 'values:edit',
  CREATE_MANUAL_ADJUSTMENT: 'values:adjust',

  // period lifecycle (owner-only)
  CLOSE_PERIOD: 'period:close',
  REOPEN_PERIOD: 'period:reopen',

  // administration (owner-only)
  CREATE_BUSINESS_UNIT: 'units:create',
  EDIT_BUSINESS_UNIT: 'units:edit',
  ARCHIVE_BUSINESS_UNIT: 'units:archive',
  MANAGE_RULES: 'rules:manage',
  MANAGE_USERS: 'users:manage',
  MANAGE_ROLES: 'roles:manage',
  MANAGE_SECURITY: 'security:manage',
  MANAGE_BRAND: 'brand:manage',
  MANAGE_SETTINGS: 'settings:manage',
  MANAGE_INTEGRATIONS: 'integrations:manage',
  VIEW_INTEGRATIONS: 'integrations:view',
} as const;

export type PermissionCode = (typeof Permission)[keyof typeof Permission];

/**
 * The four roles. Titles mirror app/core/permissions.py ROLE_TITLES.
 *
 * The general director and the accountant hold the same permissions; they are
 * separate roles because they are separate jobs.
 */
export const RoleCode = {
  OWNER: 'OWNER',
  CO_OWNER: 'CO_OWNER',
  GENERAL_DIRECTOR: 'GENERAL_DIRECTOR',
  ACCOUNTANT: 'ACCOUNTANT',
} as const;

export type RoleCodeValue = (typeof RoleCode)[keyof typeof RoleCode];

export const ROLE_TITLES: Record<string, string> = {
  OWNER: 'Владелец',
  CO_OWNER: 'Совладелец',
  GENERAL_DIRECTOR: 'Генеральный директор',
  ACCOUNTANT: 'Бухгалтер',
};

export function roleTitle(code: string | undefined): string {
  return code ? (ROLE_TITLES[code] ?? code) : '—';
}

/** Roles the owner may assign. OWNER is absent: ownership transfer is separate. */
export const ASSIGNABLE_ROLES: RoleCodeValue[] = [
  RoleCode.CO_OWNER,
  RoleCode.GENERAL_DIRECTOR,
  RoleCode.ACCOUNTANT,
];
